import { Check, ClipboardCheck, Star, X } from 'lucide-react';
import { useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useQaHistory, useReviewQa, type QaHistoryRecord } from '@/hooks/useQaHistory';
import { formatDateTime } from '@/lib/firestoreTime';

function QaItem({ qa }: { qa: QaHistoryRecord }) {
  const { user } = useAuth();
  const canReview = user?.role === 'owner' || user?.role === 'editor';
  const reviewMutation = useReviewQa();
  const [editing, setEditing] = useState(false);
  const [corrected, setCorrected] = useState(qa.adminCorrectedAnswer ?? '');

  const handleApprove = async () => {
    try {
      await reviewMutation.mutateAsync({
        id: qa.id,
        adminApproved: true,
        adminCorrectedAnswer: corrected || null,
      });
      toast.success('承認しました');
      setEditing(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleReject = async () => {
    try {
      await reviewMutation.mutateAsync({
        id: qa.id,
        adminApproved: false,
        adminCorrectedAnswer: null,
      });
      toast.success('却下しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="accent">{qa.inquiryType}</Badge>
          {qa.rating != null && (
            <Badge variant={qa.rating >= 4 ? 'approved' : qa.rating >= 3 ? 'default' : 'danger'}>
              <Star className="size-3" /> {qa.rating}
            </Badge>
          )}
          {qa.adminApproved && <Badge variant="approved">承認済み</Badge>}
          {qa.adminReviewed && !qa.adminApproved && <Badge variant="default">レビュー済み</Badge>}
          <span className="text-xs text-[var(--text-muted)]">
            使用回数: {qa.usageCount} / {formatDateTime(qa.createdAt)}
          </span>
        </div>
      </div>

      <div className="mb-3">
        <Label className="!text-xs">質問</Label>
        <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap">
          {qa.question}
        </div>
      </div>

      <div className="mb-3">
        <Label className="!text-xs">回答（AI 生成）</Label>
        <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap leading-relaxed">
          {qa.answer}
        </div>
      </div>

      {editing ? (
        <div className="mb-3">
          <Label className="!text-xs">修正版</Label>
          <Textarea
            rows={5}
            value={corrected}
            onChange={(e) => setCorrected(e.target.value)}
            placeholder="管理者による修正回答を入力"
          />
        </div>
      ) : qa.adminCorrectedAnswer ? (
        <div className="mb-3">
          <Label className="!text-xs">管理者修正版</Label>
          <div className="text-[13px] p-3 bg-[var(--accent-subtle)] border border-[var(--accent)] rounded-sm whitespace-pre-wrap leading-relaxed">
            {qa.adminCorrectedAnswer}
          </div>
        </div>
      ) : null}

      {canReview && (
        <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
          {editing ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setCorrected(qa.adminCorrectedAnswer ?? '');
                }}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="success"
                size="sm"
                onClick={handleApprove}
                disabled={reviewMutation.isPending}
              >
                <Check />
                修正を承認
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>
                修正
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={handleReject}
                disabled={reviewMutation.isPending}
              >
                <X />
                却下
              </Button>
              <Button
                type="button"
                variant="success"
                size="sm"
                onClick={handleApprove}
                disabled={reviewMutation.isPending}
              >
                <Check />
                承認
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function QaList(props: { adminReviewed?: boolean; lowRating?: boolean }) {
  const { data, isLoading } = useQaHistory({
    adminReviewed: props.adminReviewed,
    lowRating: props.lowRating,
    limit: 50,
  });

  if (isLoading) return <Spinner />;
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="該当する QA がありません"
        description={
          props.lowRating ? '低評価のフィードバックはまだありません' : '未レビューの QA はありません'
        }
      />
    );
  }
  return (
    <div className="space-y-3 max-w-4xl">
      {data.map((qa) => (
        <QaItem key={qa.id} qa={qa} />
      ))}
    </div>
  );
}

export function QaReviewPage() {
  return (
    <Screen
      title="QA レビュー"
      description="AI 応答を管理者がレビューし、修正版を登録すると次回以降の類似質問で優先されます"
    >
      <Tabs defaultValue="low">
        <TabsList>
          <TabsTrigger value="low">低評価 (rating ≤ 2)</TabsTrigger>
          <TabsTrigger value="unreviewed">未レビュー</TabsTrigger>
          <TabsTrigger value="all">すべて</TabsTrigger>
        </TabsList>
        <TabsContent value="low">
          <QaList lowRating adminReviewed={false} />
        </TabsContent>
        <TabsContent value="unreviewed">
          <QaList adminReviewed={false} />
        </TabsContent>
        <TabsContent value="all">
          <QaList />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}
